import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./Breadcrumbs.scss";
import { IUniversalItem } from "userful-chronos-app-common-js/dist/itemsControl/itemsControl";
import ClickAndDragItem from "../itemsControl/clickAndDragItem/ClickAndDragItem";
import FolderingDropdown from "../foldering/FolderingDropdown";

interface IProps {
    path: string;
    onPathUpdate: (path: string) => void;
    onDropPath?: (path: string, items: IUniversalItem[]) => void;
    hoverNavigate?: boolean;
    onIdlePath?: (path: string) => void;
    disableDropPath?: (path: string) => boolean;
}

export const BREADCRUMBS_LEGTH_BEFORE_DROPDOWN = 7;
export const BREADCRUMBS_LENGTH_AFTER_DROPDOWN = 5;

export default function Breadcrumbs(props: IProps) {
    const { t } = useTranslation();

    // Store browser timeout id as number (DOM setTimeout returns number)
    const hoverTimerRef = useRef<number | null>(null);
    const clearHoverTimer = () => {
        if (hoverTimerRef.current) {
            window.clearTimeout(hoverTimerRef.current);
            hoverTimerRef.current = null;
        }
    };
    useEffect(() => clearHoverTimer, []);

    const splitRoot: string[] = props.path.split("/");
    const segments: string[] = splitRoot.slice(0, splitRoot.length - BREADCRUMBS_LENGTH_AFTER_DROPDOWN);
    const totalNumberOfItems = splitRoot.length;

    const makeDropItem = (id: string, path: string, isDisabled: boolean): IUniversalItem => ({
        id,
        type: "folder",
        item: null,
        draggable: false,
        selectable: false,
        droppable: true,
        acceptedTypes: isDisabled ? ["__block__"] : undefined,
        onDroppableDrop: (items) => props.onDropPath?.(path, items),
        onIdle: () => props.onIdlePath?.(path),
    });

    const scheduleHoverNavigate = (path: string) => {
        if (!props.hoverNavigate) {
            return;
        }
        clearHoverTimer();
        hoverTimerRef.current = window.setTimeout(() => {
            props.onPathUpdate(path);
        }, 1000);
    };
    const handleClick = (path: string) => {
        clearHoverTimer();
        props.onPathUpdate(path);
    };

    return (
        <div className="userful-breadcrumbs noselect">
            {totalNumberOfItems >= BREADCRUMBS_LEGTH_BEFORE_DROPDOWN && (
                <>
                    <FolderingDropdown
                        size="small"
                        segments={segments}
                        onChange={(item) => {
                            props.onPathUpdate(item);
                        }}
                    />
                    <div className="svg-container-breadcrumbs">
                        <FontAwesomeIcon icon={faChevronRight} />
                    </div>
                </>
            )}
            {splitRoot.map((item, index) => {
                const notTheLastOne = index !== props.path.split("/").length - 1;
                const path = props.path
                    .split("/")
                    .slice(0, index + 1)
                    .join("/");
                const nextIsEmpty = index === 0 && props.path.split("/")[index + 1]?.length === 0;
                const disabled = !!props.disableDropPath?.(path);

                if (
                    (!nextIsEmpty || item.length > 0) &&
                    (totalNumberOfItems < BREADCRUMBS_LEGTH_BEFORE_DROPDOWN ||
                        (totalNumberOfItems >= BREADCRUMBS_LEGTH_BEFORE_DROPDOWN &&
                            index > totalNumberOfItems - (BREADCRUMBS_LENGTH_AFTER_DROPDOWN + 1)))
                ) {
                    return (
                        <div className="userful-breadcrumb" key={index}>
                            {item.length > 0 && (
                                <ClickAndDragItem item={makeDropItem(`breadcrumb-${index}`, path, disabled)}>
                                    <div
                                        onClick={(e) => {
                                            handleClick(path);
                                        }}
                                        onMouseEnter={() => scheduleHoverNavigate(path)}
                                        onMouseLeave={clearHoverTimer}
                                        className={`${notTheLastOne ? "userful-breadcrumb-active" : "userful-breadcrumb-inactive"}`}
                                    >
                                        <p>{item}</p>
                                    </div>
                                </ClickAndDragItem>
                            )}
                            {!nextIsEmpty && item.length === 0 && (
                                <ClickAndDragItem
                                    item={makeDropItem("breadcrumb-root", "/", !!props.disableDropPath?.("/"))}
                                >
                                    <div
                                        onClick={(e) => {
                                            handleClick("/");
                                        }}
                                        onMouseEnter={() => {
                                            scheduleHoverNavigate("/");
                                        }}
                                        onMouseLeave={clearHoverTimer}
                                        className={`${notTheLastOne ? "userful-breadcrumb-active" : "userful-breadcrumb-inactive"}`}
                                    >
                                        <p>{t("Shared.index")}</p>
                                    </div>
                                </ClickAndDragItem>
                            )}
                            {!nextIsEmpty && notTheLastOne && (
                                <div className="userful-breadcrumb-icon">
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </div>
                            )}
                        </div>
                    );
                }
            })}
        </div>
    );
}
